/**
 * Draft Tasks store - manages AI inbox state
 */

import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import {
  draftTasksApi,
  type DraftTask,
  type DraftTaskDetail,
  type AIInboxStats,
  type DraftTaskFilterParams,
  type CreateDraftTaskRequest,
  type ApproveDraftTaskRequest,
  type RejectDraftTaskRequest,
  type ApprovalResponse,
} from '@services/api/draftTasks';

interface DraftTasksState {
  // State
  draftTasks: DraftTask[];
  currentDraftTask: DraftTaskDetail | null;
  stats: AIInboxStats | null;
  selectedIds: Set<string>;
  isLoading: boolean;
  isApproving: boolean;
  error: string | null;
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
  filters: Omit<DraftTaskFilterParams, 'organizationId'>;

  // Actions
  fetchStats: (organizationId: string) => Promise<void>;
  fetchDraftTasks: (organizationId: string, page?: number) => Promise<void>;
  fetchDraftTask: (draftTaskId: string) => Promise<void>;
  createDraftTask: (data: CreateDraftTaskRequest) => Promise<DraftTask>;
  approveDraftTask: (draftTaskId: string, data: ApproveDraftTaskRequest) => Promise<ApprovalResponse>;
  rejectDraftTask: (draftTaskId: string, data?: RejectDraftTaskRequest) => Promise<void>;
  bulkApprove: (draftTaskIds: string[], boardId: string, columnId: string, assigneeId?: string) => Promise<void>;
  bulkReject: (draftTaskIds: string[], reviewNotes?: string) => Promise<void>;
  bulkDelete: (draftTaskIds: string[]) => Promise<void>;
  deleteDraftTask: (draftTaskId: string) => Promise<void>;
  toggleSelection: (draftTaskId: string) => void;
  selectAll: () => void;
  clearSelection: () => void;
  setFilters: (filters: Partial<DraftTasksState['filters']>) => void;
  clearError: () => void;
  reset: () => void;
}

const initialState = {
  draftTasks: [] as DraftTask[],
  currentDraftTask: null as DraftTaskDetail | null,
  stats: null as AIInboxStats | null,
  selectedIds: new Set<string>(),
  isLoading: false,
  isApproving: false,
  error: null as string | null,
  total: 0,
  page: 1,
  pageSize: 50,
  hasMore: false,
  filters: {} as Omit<DraftTaskFilterParams, 'organizationId'>,
};

export const useDraftTasksStore = create<DraftTasksState>()(
  immer((set, get) => ({
    ...initialState,

    fetchStats: async (organizationId: string) => {
      try {
        const stats = await draftTasksApi.getStats(organizationId);
        set((state) => {
          state.stats = stats;
        });
      } catch (error) {
        set((state) => {
          state.error = error instanceof Error ? error.message : 'Failed to fetch stats';
        });
      }
    },

    fetchDraftTasks: async (organizationId: string, page = 1) => {
      set((state) => {
        state.isLoading = true;
        state.error = null;
      });

      try {
        const { filters, pageSize } = get();
        const response = await draftTasksApi.list(
          { organizationId, ...filters },
          page,
          pageSize
        );

        set((state) => {
          state.draftTasks = response.items;
          state.total = response.total;
          state.page = response.page;
          state.hasMore = response.hasMore;
          state.isLoading = false;
        });
      } catch (error) {
        set((state) => {
          state.error = error instanceof Error ? error.message : 'Failed to fetch draft tasks';
          state.isLoading = false;
        });
      }
    },

    fetchDraftTask: async (draftTaskId: string) => {
      set((state) => {
        state.isLoading = true;
        state.error = null;
      });

      try {
        const draftTask = await draftTasksApi.get(draftTaskId);
        set((state) => {
          state.currentDraftTask = draftTask;
          state.isLoading = false;
        });
      } catch (error) {
        set((state) => {
          state.error = error instanceof Error ? error.message : 'Failed to fetch draft task';
          state.isLoading = false;
        });
      }
    },

    createDraftTask: async (data: CreateDraftTaskRequest) => {
      set((state) => {
        state.isLoading = true;
        state.error = null;
      });

      try {
        const draftTask = await draftTasksApi.create(data);
        set((state) => {
          state.draftTasks.unshift(draftTask);
          state.total += 1;
          if (state.stats) {
            state.stats.pendingCount += 1;
            state.stats.totalCount += 1;
          }
          state.isLoading = false;
        });
        return draftTask;
      } catch (error) {
        set((state) => {
          state.error = error instanceof Error ? error.message : 'Failed to create draft task';
          state.isLoading = false;
        });
        throw error;
      }
    },

    approveDraftTask: async (draftTaskId: string, data: ApproveDraftTaskRequest) => {
      set((state) => {
        state.isApproving = true;
        state.error = null;
      });

      try {
        const response = await draftTasksApi.approve(draftTaskId, data);
        set((state) => {
          // Remove from list
          state.draftTasks = state.draftTasks.filter((dt) => dt.id !== draftTaskId);
          state.total -= 1;
          state.selectedIds.delete(draftTaskId);

          // Update stats
          if (state.stats) {
            state.stats.pendingCount -= 1;
            state.stats.approvedCount += 1;
          }

          state.isApproving = false;
        });
        return response;
      } catch (error) {
        set((state) => {
          state.error = error instanceof Error ? error.message : 'Failed to approve draft task';
          state.isApproving = false;
        });
        throw error;
      }
    },

    rejectDraftTask: async (draftTaskId: string, data?: RejectDraftTaskRequest) => {
      set((state) => {
        state.isApproving = true;
        state.error = null;
      });

      try {
        await draftTasksApi.reject(draftTaskId, data || {});
        set((state) => {
          // Remove from list (if viewing pending)
          state.draftTasks = state.draftTasks.filter((dt) => dt.id !== draftTaskId);
          state.total -= 1;
          state.selectedIds.delete(draftTaskId);

          // Update stats
          if (state.stats) {
            state.stats.pendingCount -= 1;
            state.stats.rejectedCount += 1;
          }

          state.isApproving = false;
        });
      } catch (error) {
        set((state) => {
          state.error = error instanceof Error ? error.message : 'Failed to reject draft task';
          state.isApproving = false;
        });
        throw error;
      }
    },

    bulkApprove: async (draftTaskIds: string[], boardId: string, columnId: string, assigneeId?: string) => {
      set((state) => {
        state.isApproving = true;
        state.error = null;
      });

      try {
        const response = await draftTasksApi.bulkApprove({
          draftTaskIds,
          boardId,
          columnId,
          assigneeId,
        });

        set((state) => {
          // Remove approved tasks from list
          const idsSet = new Set(draftTaskIds);
          state.draftTasks = state.draftTasks.filter((dt) => !idsSet.has(dt.id));
          state.total -= response.approvedCount;
          state.selectedIds.clear();

          // Update stats
          if (state.stats) {
            state.stats.pendingCount -= response.approvedCount;
            state.stats.approvedCount += response.approvedCount;
          }

          state.isApproving = false;
        });
      } catch (error) {
        set((state) => {
          state.error = error instanceof Error ? error.message : 'Failed to bulk approve';
          state.isApproving = false;
        });
        throw error;
      }
    },

    bulkReject: async (draftTaskIds: string[], reviewNotes?: string) => {
      set((state) => {
        state.isApproving = true;
        state.error = null;
      });

      try {
        const response = await draftTasksApi.bulkReject({
          draftTaskIds,
          reviewNotes,
        });

        set((state) => {
          // Remove rejected tasks from list
          const idsSet = new Set(draftTaskIds);
          state.draftTasks = state.draftTasks.filter((dt) => !idsSet.has(dt.id));
          state.total -= response.rejectedCount;
          state.selectedIds.clear();

          // Update stats
          if (state.stats) {
            state.stats.pendingCount -= response.rejectedCount;
            state.stats.rejectedCount += response.rejectedCount;
          }

          state.isApproving = false;
        });
      } catch (error) {
        set((state) => {
          state.error = error instanceof Error ? error.message : 'Failed to bulk reject';
          state.isApproving = false;
        });
        throw error;
      }
    },

    bulkDelete: async (draftTaskIds: string[]) => {
      set((state) => {
        state.isApproving = true;
        state.error = null;
      });

      try {
        const response = await draftTasksApi.bulkDelete({
          draftTaskIds,
        });

        set((state) => {
          // Remove deleted tasks from list
          const idsSet = new Set(draftTaskIds);
          const deletedTasks = state.draftTasks.filter((dt) => idsSet.has(dt.id));
          state.draftTasks = state.draftTasks.filter((dt) => !idsSet.has(dt.id));
          state.total -= response.deletedCount;
          state.selectedIds.clear();

          // Update stats based on the deleted tasks' statuses
          if (state.stats) {
            for (const task of deletedTasks) {
              state.stats.totalCount -= 1;
              if (task.status === 'pending') {
                state.stats.pendingCount -= 1;
              } else if (task.status === 'approved' || task.status === 'modified') {
                state.stats.approvedCount -= 1;
              } else if (task.status === 'rejected') {
                state.stats.rejectedCount -= 1;
              }
            }
          }

          state.isApproving = false;
        });
      } catch (error) {
        set((state) => {
          state.error = error instanceof Error ? error.message : 'Failed to bulk delete';
          state.isApproving = false;
        });
        throw error;
      }
    },

    deleteDraftTask: async (draftTaskId: string) => {
      try {
        await draftTasksApi.delete(draftTaskId);
        set((state) => {
          const draftTask = state.draftTasks.find((dt) => dt.id === draftTaskId);
          state.draftTasks = state.draftTasks.filter((dt) => dt.id !== draftTaskId);
          state.total -= 1;
          state.selectedIds.delete(draftTaskId);

          // Update stats based on the task's status
          if (state.stats && draftTask) {
            state.stats.totalCount -= 1;
            if (draftTask.status === 'pending') {
              state.stats.pendingCount -= 1;
            } else if (draftTask.status === 'approved' || draftTask.status === 'modified') {
              state.stats.approvedCount -= 1;
            } else if (draftTask.status === 'rejected') {
              state.stats.rejectedCount -= 1;
            }
          }
        });
      } catch (error) {
        set((state) => {
          state.error = error instanceof Error ? error.message : 'Failed to delete draft task';
        });
        throw error;
      }
    },

    toggleSelection: (draftTaskId: string) => {
      set((state) => {
        if (state.selectedIds.has(draftTaskId)) {
          state.selectedIds.delete(draftTaskId);
        } else {
          state.selectedIds.add(draftTaskId);
        }
      });
    },

    selectAll: () => {
      set((state) => {
        state.selectedIds = new Set(state.draftTasks.map((dt) => dt.id));
      });
    },

    clearSelection: () => {
      set((state) => {
        state.selectedIds.clear();
      });
    },

    setFilters: (filters) => {
      set((state) => {
        state.filters = { ...state.filters, ...filters };
      });
    },

    clearError: () => {
      set((state) => {
        state.error = null;
      });
    },

    reset: () => {
      set(initialState);
    },
  }))
);

// Selectors
export const selectPendingCount = (state: DraftTasksState) => state.stats?.pendingCount ?? 0;
export const selectSelectedCount = (state: DraftTasksState) => state.selectedIds.size;
export const selectIsAllSelected = (state: DraftTasksState) =>
  state.draftTasks.length > 0 && state.selectedIds.size === state.draftTasks.length;
